import { EffectsMap } from 'use-effect-reducer'
import { ApiTransaction } from '@vend/types'
import {
  LSPAY_ERROR,
  LSPAY_ERROR_MESSAGES,
  LSPAY_PRODUCT_VERSION,
  LSPAY_TRANSACTION_STATUS,
  SWIPER_ERROR_CODES,
} from '../../helpers/lspayConstants'
import { createCnpPmofSession } from '../../helpers/createSession'
import { confirmPaymentState } from '../../helpers/confirmPaymentState'
import {
  LSPAYCnpPmofPaymentEffect,
  LSPAYCnpPmofPaymentEffectsMapArgs,
  LSPAYCnpPmofPaymentEvent,
  LSPAYCnpPmofPaymentState,
} from './types'
import {
  createGenericPoller,
  defaultPollingConfig,
  defaultTimer,
  PollingConfig,
  TimerInterface,
  WorkFunction,
  WorkResult,
} from './poller'

// Specific poller for payment state confirmation
export const createPaymentStatePoller = (
  registerId: string,
  registerSalePaymentId: string,
  paymentId: string,
  customerId?: string,
  retailerPaymentTypeId?: string,
  timer: TimerInterface = defaultTimer,
  config: PollingConfig = defaultPollingConfig
) => {
  const workFunction: WorkFunction<WorkResult<ApiTransaction>> = async () => {
    const response = await confirmPaymentState(
      registerId,
      registerSalePaymentId,
      paymentId,
      customerId,
      retailerPaymentTypeId
    )

    if (response.status === LSPAY_TRANSACTION_STATUS.COMPLETED) {
      return {
        isSuccess: true,
        data: response.transaction,
      }
    } else {
      return {
        isSuccess: false,
        error: 'Payment not confirmed',
      }
    }
  }

  return createGenericPoller(workFunction, timer, config)
}

export const getCnpPmofSessionPaymentEffectsMap = (
  args: LSPAYCnpPmofPaymentEffectsMapArgs,
  timer: TimerInterface = defaultTimer
): EffectsMap<
  LSPAYCnpPmofPaymentState,
  LSPAYCnpPmofPaymentEvent,
  LSPAYCnpPmofPaymentEffect
> => {
  const {
    register,
    amount,
    registerSaleId,
    registerSalePaymentId,
    customerId,
    retailerPaymentTypeId,
    setSwiperError,
  } = args

  const { id: registerId, name: registerName } = register

  return {
    createSessionEffect: (_state, _effect, dispatch) => {
      createCnpPmofSession({
        amount: amount.toNumber(),
        registerSalePaymentId,
        customerId,
        registerId,
        retailerPaymentTypeId,
        metadata: {
          deviceId: registerId,
          deviceName: registerName,
          hrOrderId: registerSaleId,
          hrPaymentId: registerSalePaymentId,
          salesChannel: 'IN_STORE',
          productVersion: LSPAY_PRODUCT_VERSION,
        },
      })
        .then(({ session, paymentId }) => {
          dispatch({
            type: 'SESSION_CREATED',
            session,
            paymentId,
          })
        })
        .catch(error => {
          if (Object.values(SWIPER_ERROR_CODES).includes(error.code)) {
            setSwiperError(error)
            return
          }

          dispatch({
            type: 'ERROR',
            errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_PAYMENT],
          })
        })
    },
    confirmPaymentStateEffect: (state, effect, dispatch) => {
      const paymentId =
        state.status === 'confirmingPaymentState' ? state.paymentId : null

      if (!paymentId) {
        dispatch({
          type: 'ERROR',
          errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_PAYMENT],
        })
        return
      }

      const context = effect.context || {
        startTime: timer.now(),
        currentDelay: defaultPollingConfig.initialDelayMs,
        attemptCount: 0,
      }

      const poller = createPaymentStatePoller(
        registerId,
        registerSalePaymentId,
        paymentId,
        customerId,
        retailerPaymentTypeId,
        timer
      )

      poller(
        context,
        transaction => {
          dispatch({
            type: 'API_STATE_VERIFIED',
            transaction,
          })
        },
        () => {
          dispatch({
            type: 'CONFIRM_PAYMENT_STATE_TIMEOUT',
          })
        },
        errorMessage => {
          dispatch({
            type: 'ERROR',
            errorMessage,
          })
        },
        newContext => {
          dispatch({
            type: 'RETRY_CONFIRMING',
            context: newContext,
          })
        }
      )
    },
  }
}
